import React from "react";

/**
 * MetallicCard (Class Card Layout with linear + radial gradient background)
 *
 * Props:
 * - id: string | number (e.g., 107377)
 * - name: string (person's name)
 * - validUntil: string (date or label)
 * - variant?: "steel" | "titanium" | "gold"
 * - className?: string
 */
export default function MetallicCard({
  id = "",
  name = "",
  validUntil = "",
  variant = "steel",
  className = "",
}) {
  const VARIANTS = {
    steel: {
      rim: "rgba(255,255,255,0.35)",
      shadow: "rgba(0,0,0,0.35)",
    },
    titanium: {
      rim: "rgba(255,255,255,0.33)",
      shadow: "rgba(0,0,0,0.38)",
    },
    gold: {
      rim: "rgba(255,255,255,0.38)",
      shadow: "rgba(0,0,0,0.32)",
    },
  };
  const v = VARIANTS[variant] ?? VARIANTS.steel;

  // Combined background: linear + radial
  const bgStyle = {
    backgroundImage: `
      linear-gradient(90deg,
        #4D5455 1%,
        #9EA1A1 5%,
        #848B8A 30%,
        #b1b5b5 65%,
        #7e8b8f 88%,
        #a0a7a8 96%,
        #ffffff 100%),
      radial-gradient(circle at center,
        rgba(200, 200, 200,1) 0%,
        rgba(200, 200, 200,0) 100%)`,
    backgroundBlendMode: "overlay",
    backgroundSize: "100% 100%, 100% 100%",
    backgroundPosition: "center",
  };

  const noiseStyle = {
    backgroundImage:
      "repeating-linear-gradient(0deg, rgba(0,0,0,0.02) 0, rgba(0,0,0,0.02) 1px, rgba(255,255,255,0.02) 1px, rgba(255,255,255,0.02) 2px)",
    mixBlendMode: "overlay",
    opacity: 0.35,
    pointerEvents: "none",
  };

  const glossStyle = {
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.12) 12%, rgba(255,255,255,0.06) 40%, rgba(0,0,0,0.06) 55%, rgba(0,0,0,0.12) 100%)",
    maskImage:
      "radial-gradient(120% 80% at 50% -10%, rgba(0,0,0,1) 40%, rgba(0,0,0,0) 70%)",
    WebkitMaskImage:
      "radial-gradient(120% 80% at 50% -10%, rgba(0,0,0,1) 40%, rgba(0,0,0,0) 70%)",
    pointerEvents: "none",
  };

  const BOTTOM_RADIUS = 28;
  const cardRadius = {
    borderBottomLeftRadius: BOTTOM_RADIUS,
    borderBottomRightRadius: BOTTOM_RADIUS,
  };

  // Engraved / inner-shadow style text
  const engraved = () => ({
    color: "#363636",
    textShadow: [
      "-0.5px 0.5px 0.5px #FFFFFF", // dark inner shadow
      "-0.5px 0.5px 0.5px #000000", // light highlight inner shadow
    ].join(","),
    letterSpacing: "0.02em",
  });

  return (
    <div
      className={[
        "relative w-full max-w-xl mx-auto ",
        "pt-[1px]",
        "bg-gradient-to-br from-gray-600 via-white to-gray-100",
        "shadow-[0_10px_30px_rgba(0,0,0,0.1)]",
        className,
      ].join(" ")}
      style={{ ...cardRadius, backdropFilter: "blur(2px)" }}
    >
      <div
        className="relative overflow-hidden"
        style={{ ...bgStyle, ...cardRadius }}
      >
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            ...cardRadius,
            boxShadow:
              "inset 0 0px 0 rgba(255,255,255,0.35), inset 0 -1px 0 rgba(0,0,0,0.25), inset 0 0 30px rgba(0,0,0,0.18)",
          }}
        />
        <div className="absolute inset-0" style={{ ...glossStyle, ...cardRadius }} />
        <div className="absolute inset-0" style={{ ...noiseStyle, ...cardRadius }} />

        {/* Content rows */}
        <div className="relative px-7 py-5 space-y-0 ">
          {/* Row 1: Name label + ID */}
          <div className="grid grid-cols-3 items-baseline">
            <div
              className="col-span-2 text-sm font-medium whitespace-nowrap overflow-hidden text-ellipsis"
              style={engraved()}
            >
              Name:
            </div>
            <div
              className="col-span-1 text-right text-sm font-medium whitespace-nowrap flex items-baseline justify-end gap-1"
              style={engraved()}
            >
              <span className="whitespace-nowrap">ID:</span>
              <span
                className="inline-block font-mono tabular-nums whitespace-nowrap"
                style={{ width: "8ch", textAlign: "left", letterSpacing: "0.06em" }}
              >
                {String(id || "").slice(0, 8)}
              </span>
            </div>
          </div>

          {/* Row 2: Person's name */}
          <div
            className="w-full text-xl md:text-mdl font-semibold whitespace-nowrap overflow-hidden text-ellipsis"
            style={engraved()}
          >
            {name}
          </div>

          {/* Row 3: Valid until */}
          <div
            className="w-full text-xs md:text-md whitespace-nowrap overflow-hidden text-ellipsis"
            style={engraved()}
          >
            Valid until: {validUntil}
          </div>
        </div>
      </div>
    </div>
  );
}
